import { Loader2 } from "lucide-react";
import { Button, LicensePlateInput } from "@rumbledirect/ui-v2";
import {
  useLicensePlateLookup,
  type LicensePlateLookupResult,
} from "@/dossiers/useLicensePlateLookup";
import type { LicensePlateQuestionModel } from "./models";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for the schema's `licensePlate` question type, pairing admin-v2's
 * `LicensePlateInput` (uppercase masking, NL sidecode grouping) with the
 * same `useLicensePlateLookup` RDW confirmation `DossierCreateSheet` uses,
 * so both forms behave identically: lookup on blur and on Enter, a result
 * card on success, and a non-blocking notice when nothing is found.
 *
 * The value written back to the question matches the backend `LicensePlate`
 * embeddable (`{country, identifier}`), so the schema's
 * `payloadPath: "licensePlate"` lands the shape `RepairDirectGenericDossierIn`
 * expects. `country` comes from the model's `country` property — the seeded
 * schema does not set it, so it defaults to `"NL"`, matching the hardcoded
 * country in `toCreateDossierInput`/`toConceptInviteInput`.
 */
export function SurveyLicensePlateQuestion({
  question,
}: {
  readonly question: LicensePlateQuestionModel;
}) {
  useSurveyQuestionRerender(question);
  const lookup = useLicensePlateLookup();

  const questionValue = question.value as
    | { country?: string; identifier?: string }
    | null
    | undefined;
  const identifier = questionValue?.identifier ?? "";

  const commitValue = (next: string) => {
    question.value =
      next === "" ? undefined : { country: question.country, identifier: next };
  };

  const runLookup = () => {
    // Look up the bare identifier (no display hyphens), as the registration
    // API expects, gated on the minimum plausible NL plate length.
    const bare = identifier.replace(/[^A-Z0-9]/gi, "");
    if (bare.length >= 6) {
      lookup.lookup(question.country, bare);
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <LicensePlateInput
        id={question.inputId}
        placeholder="XX-99-XX"
        value={identifier}
        disabled={question.isDisabledAttr}
        readOnly={question.isReadOnlyAttr}
        onChange={(event) => {
          commitValue(event.target.value);
          // Any edit invalidates a previous lookup result — it was for
          // whatever plate was there before.
          lookup.reset();
        }}
        onKeyDown={(event) => {
          // Enter confirms the plate without advancing the form.
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        onBlur={(event) => {
          question.onBlur(event);
          runLookup();
        }}
        onFocus={(event) => question.onFocus(event)}
        aria-label={
          question.a11y_input_ariaLabel ?? question.title ?? question.name
        }
        aria-required={question.a11y_input_ariaRequired}
        aria-invalid={question.a11y_input_ariaInvalid}
        aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
      />
      <VehicleLookupStatus lookup={lookup} retry={runLookup} />
    </div>
  );
}

/**
 * A polite live region announces the lookup as it settles.
 */
function VehicleLookupStatus({
  lookup,
  retry,
}: {
  readonly lookup: {
    readonly result: LicensePlateLookupResult | undefined;
    readonly isLoading: boolean;
    readonly notFound: boolean;
    readonly isError: boolean;
  };
  readonly retry: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-10 min-w-0 flex-1 items-center text-sm"
    >
      {lookup.result ? (
        <div className="flex min-h-10 min-w-0 items-baseline gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2">
          <span className="truncate font-semibold text-foreground">
            {[lookup.result.brand, lookup.result.model]
              .filter(Boolean)
              .join(" ") || "Onbekend voertuig"}
          </span>
          <span className="shrink-0 text-muted-foreground">
            Bouwjaar {lookup.result.buildYear ?? "—"}
          </span>
        </div>
      ) : lookup.isLoading ? (
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Voertuiggegevens ophalen...
        </span>
      ) : lookup.notFound ? (
        <span className="text-muted-foreground">
          Voor dit kenteken zijn geen voertuiggegevens beschikbaar.
        </span>
      ) : lookup.isError ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-destructive">
            Ophalen van voertuiggegevens is mislukt.
          </span>
          <Button variant="outline" size="sm" onClick={retry}>
            Opnieuw proberen
          </Button>
        </div>
      ) : null}
    </div>
  );
}
